import React, { useState, useRef, useEffect } from 'react';
import { Youtube, MonitorPlay, Link as LinkIcon, AlertCircle, X, Maximize2, ArrowRight, ExternalLink, Popcorn, Camera, FileText, Upload, ChevronUp, ChevronDown } from 'lucide-react';
import { Language, Stroke } from '../types';
import { PdfViewer, PdfDocumentUtils } from './PdfViewer';

interface MediaPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSnap: (imageBase64: string) => void;
  onPageChange?: (page: number) => void;
  pdfUrl?: string | null;
  onUploadPdf?: (file: File) => void;
  language: Language;
  annotations?: { [pageNumber: number]: Stroke[] };
  onSaveAnnotations?: (pageNumber: number, strokes: Stroke[]) => void;
  onGenerateNotes?: (pdfText: string) => void;
  userEmail?: string;
  documentTitle?: string;
  boardTitle?: string;
  autoSwitchToPdf?: boolean;
  onDocumentReady?: (utils: PdfDocumentUtils) => void;
  onGuidedReading?: (pageText: string, pageNumber: number) => void;
  guidedReadingHighlights?: { text: string; colorIndex: number }[];
  isGuidedReadingLoading?: boolean;
  highlightCaptureButton?: boolean;
  isMobile?: boolean;
  onVoiceCapture?: (imageBase64: string, transcript: string) => void;
  selectedNoteName?: string;
}

export const MediaPanel: React.FC<MediaPanelProps> = ({
  isOpen,
  onClose,
  onSnap,
  onPageChange,
  pdfUrl,
  onUploadPdf,
  language,
  annotations,
  onSaveAnnotations,
  onGenerateNotes,
  userEmail,
  documentTitle,
  boardTitle,
  autoSwitchToPdf,
  onDocumentReady,
  onGuidedReading,
  guidedReadingHighlights,
  isGuidedReadingLoading,
  highlightCaptureButton,
  isMobile,
  onVoiceCapture,
  selectedNoteName,
}) => {
  const [activeTab, setActiveTab] = useState<'youtube' | 'pdf'>('pdf');
  const [showTabs, setShowTabs] = useState(true);
  const [toolbarContainer, setToolbarContainer] = useState<HTMLDivElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [videoId, setVideoId] = useState<string | null>(null);
  const [isCaptureReady, setIsCaptureReady] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && (pdfUrl || autoSwitchToPdf)) {
      setActiveTab('pdf');
    }
  }, [isOpen, pdfUrl, autoSwitchToPdf]);

  const labels = {
    english: {
      youtube: "YouTube",
      pdf: "PDF Reader",
      pasteLink: "Paste YouTube Link",
      load: "Load Video",
      enableCapture: "Enable Snapshot",
      snap: "Snap Screenshot",
      captureInst: "Required for screenshots",
      tip: "Paste a link (including Shorts) to start.",
      invalidLink: "Invalid YouTube Link",
      openExt: "Open on YouTube",
      popout: "Pop-out Player",
      popoutTip: "Video blocked? Open in a separate window.",
      uploadPdf: "Upload PDF",
      noPdf: "No PDF loaded",
      changePdf: "Change PDF",
      hideTabs: "Hide Tabs",
      showTabs: "Show Tabs"
    },
    vietnamese: {
      youtube: "YouTube",
      pdf: "Đọc PDF",
      pasteLink: "Dán link YouTube",
      load: "Tải Video",
      enableCapture: "Bật chế độ Chụp",
      snap: "Chụp Ảnh",
      captureInst: "Cần để chụp ảnh",
      tip: "Dán link để xem.",
      invalidLink: "Link không hợp lệ",
      openExt: "Mở trên YouTube",
      popout: "Mở Cửa Sổ Riêng",
      popoutTip: "Video bị chặn? Mở cửa sổ riêng để xem.",
      uploadPdf: "Tải PDF lên",
      noPdf: "Chưa có PDF",
      changePdf: "Đổi PDF",
      hideTabs: "Ẩn thanh Tab",
      showTabs: "Hiện thanh Tab"
    }
  };

  const t = labels[language];

  const getYouTubeId = (url: string) => {
    if (!url) return null;
    const regExp = /^.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/)|(?:(?:watch)?\?v(?:i)?=|\&v(?:i)?=))([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[1].length === 11) ? match[1] : null;
  };

  const handleLoadVideo = () => {
    if (!inputValue.trim()) return;
    const id = getYouTubeId(inputValue);

    if (id) {
        setVideoId(id);
        setInputValue('');
    } else {
        alert(t.invalidLink);
    }
  };

  const handlePopout = () => {
    if (!videoId) return;
    const width = 600;
    const height = 400;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);
    window.open(
        `https://www.youtube.com/embed/${videoId}?autoplay=1`,
        'MindSyncPlayer',
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=yes, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`
    );
  };

  const startCapture = async () => {
    try {
      setStreamError(null);
      // @ts-ignore
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "browser" },
        audio: false,
        selfBrowserSurface: "include"
      } as any);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      streamRef.current = stream;
      setIsCaptureReady(true);

      stream.getVideoTracks()[0].onended = () => {
        setIsCaptureReady(false);
        streamRef.current = null;
      };

    } catch (err) {
      console.error("Error sharing screen:", err);
      setStreamError(language === 'vietnamese' ? "Bạn cần chọn 'Tab Này' (This Tab) khi chia sẻ để chụp ảnh." : "Permission denied or cancelled. Please select 'This Tab' to enable snapshots.");
    }
  };

  const handleSnapYoutube = () => {
    if (!panelRef.current || !videoRef.current || !canvasRef.current) return;

    const panelRect = panelRef.current.getBoundingClientRect();
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    const videoW = video.videoWidth;
    const videoH = video.videoHeight;
    const windowW = window.innerWidth;
    const windowH = window.innerHeight;

    const scaleX = videoW / windowW;
    const scaleY = videoH / windowH;

    const cropX = panelRect.left * scaleX;
    const cropY = panelRect.top * scaleY;
    const cropW = panelRect.width * scaleX;
    const cropH = panelRect.height * scaleY;

    canvas.width = cropW;
    canvas.height = cropH;

    ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
    const base64 = canvas.toDataURL('image/png');
    onSnap(base64);
  };

  const handlePdfUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0] && onUploadPdf) {
          onUploadPdf(e.target.files[0]);
      }
      // Reset input value to allow re-selection of the same file
      e.target.value = '';
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-50 dark:bg-slate-900 relative border-l dark:border-slate-800 overflow-hidden">

      <video ref={videoRef} className="hidden" autoPlay playsInline muted />
      <canvas ref={canvasRef} className="hidden" />

      {/* Hidden File Input (Always Mounted) */}
      <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={handlePdfUpload}
      />

      {/* Tabs Header - Reduced padding to pt-2 */}
      {showTabs && (
        <div className="flex items-center justify-between px-3 pt-2 pb-2 bg-white dark:bg-slate-950 border-b dark:border-slate-800 shrink-0 z-30">
            <div className="flex gap-2 shrink-0">
               <button
                 onClick={() => setActiveTab('pdf')}
                 className={`px-3 md:px-4 py-2 text-sm font-bold rounded-t-xl transition-all flex items-center gap-2 ${
                     activeTab === 'pdf'
                     ? 'bg-gray-50 dark:bg-slate-900 text-blue-600 border-t border-x dark:border-slate-700 relative -mb-[1px] shadow-sm'
                     : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-800 dark:hover:text-gray-200'
                 }`}
               >
                  <FileText size={16} />
                  {t.pdf}
               </button>
            </div>

            {/* PDF annotation tools render here via portal from PdfViewer */}
            <div ref={activeTab === 'pdf' ? setToolbarContainer : undefined} className="flex-1 flex items-center justify-center min-w-0 overflow-x-auto hide-scrollbar" />

            <div className="flex items-center gap-1 shrink-0">
               <button
                  onClick={() => setShowTabs(false)}
                  className="p-2 mb-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  title={t.hideTabs}
               >
                  <ChevronUp size={18} />
               </button>
               {activeTab === 'pdf' && (
                   <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 mb-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title={t.changePdf}
                   >
                      <Upload size={18} />
                   </button>
               )}
               <button
                  onClick={onClose}
                  className="p-2 mb-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Close Panel"
               >
                  <X size={18} />
               </button>
            </div>
        </div>
      )}

      {/* Floating Show Tabs Button */}
      {!showTabs && (
          <button
             onClick={() => setShowTabs(true)}
             className="absolute top-2 right-2 p-2 bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-lg shadow-lg z-[60] text-gray-500 hover:text-blue-500 border dark:border-slate-700 transition-all"
             title={t.showTabs}
          >
              <ChevronDown size={18} />
          </button>
      )}

      {activeTab === 'youtube' ? (
          <>
            <div className="px-4 py-3 border-b dark:border-slate-700 bg-white dark:bg-slate-900 flex justify-between items-center shadow-sm z-20 shrink-0">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest hidden sm:block">
                        YOUTUBE
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                    {!isCaptureReady ? (
                        <button
                            onClick={startCapture}
                            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-300 border border-indigo-100 dark:border-slate-600 rounded-lg text-xs font-bold hover:bg-indigo-100 dark:hover:bg-slate-700 transition-colors"
                            title={t.captureInst}
                        >
                        <MonitorPlay size={14} />
                        {t.enableCapture}
                        </button>
                    ) : (
                        <button
                            onClick={handleSnapYoutube}
                            className="flex items-center gap-2 px-4 py-1.5 bg-red-500 text-white rounded-lg text-xs font-bold hover:bg-red-600 transition-colors shadow-md animate-in pulse"
                        >
                        <Camera size={14} />
                        {t.snap}
                        </button>
                    )}
                    </div>
            </div>

            <div ref={panelRef} className="flex-1 relative overflow-hidden bg-gray-50 dark:bg-slate-900">
                <div className="absolute inset-0 flex flex-col animate-in fade-in duration-300">
                    {!videoId ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gray-50 dark:bg-slate-900">

                        <div className="w-full max-w-sm bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700">
                            <div className="w-12 h-12 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
                                <Youtube size={24} />
                            </div>

                            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">{t.load}</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">{t.tip}</p>

                            <div className="space-y-3">
                                <div className="relative group">
                                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={16} />
                                    <input
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleLoadVideo()}
                                        placeholder={t.pasteLink}
                                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none text-sm dark:text-white transition-all"
                                    />
                                </div>
                                <button
                                    onClick={handleLoadVideo}
                                    disabled={!inputValue}
                                    className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
                                >
                                    {t.load} <ArrowRight size={16} />
                                </button>
                            </div>
                        </div>

                    </div>
                    ) : (
                    <div className="flex-1 bg-black relative flex flex-col justify-center w-full h-full group">
                        <div className="absolute inset-0">
                            <iframe
                                className="w-full h-full border-none"
                                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                                title="YouTube video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            />
                        </div>

                        <div className="absolute top-4 right-16 flex gap-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={handlePopout}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors shadow-lg"
                                title={t.popoutTip}
                            >
                                <Popcorn size={12} /> {t.popout}
                            </button>
                            <a
                                href={`https://www.youtube.com/watch?v=${videoId}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-black/60 hover:bg-black/80 backdrop-blur text-white text-xs rounded-lg transition-colors"
                            >
                                <ExternalLink size={12} /> {t.openExt}
                            </a>
                        </div>

                        <button
                            onClick={() => {
                                setVideoId(null);
                            }}
                            className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-red-600/90 text-white rounded-full backdrop-blur-md transition-all z-20 opacity-0 group-hover:opacity-100"
                            title="Close Video"
                        >
                            <X size={16} />
                        </button>
                    </div>
                    )}
                </div>
                {streamError && (
                <div className="absolute inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 text-center animate-in fade-in">
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl max-w-sm shadow-2xl border dark:border-slate-700 transform scale-100 animate-in zoom-in-95 duration-200">
                        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle size={24} />
                        </div>
                        <h4 className="font-bold text-lg mb-2 dark:text-white">Access Denied</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">{streamError}</p>
                        <button
                        onClick={() => setStreamError(null)}
                        className="w-full py-2.5 bg-gray-900 dark:bg-white dark:text-slate-900 text-white rounded-xl font-bold hover:opacity-90 transition-opacity"
                        >
                        Understood
                        </button>
                    </div>
                </div>
                )}
            </div>
          </>
      ) : (
          <div className="flex-1 flex flex-col relative bg-gray-100 dark:bg-slate-900 overflow-hidden">
              {!pdfUrl ? (
                  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                      <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-3xl flex items-center justify-center mb-6">
                          <FileText size={40} className="text-blue-500" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{t.uploadPdf}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 max-w-xs">{language === 'vietnamese' ? 'Tải lên tài liệu PDF để đọc và chụp ảnh ghi chú.' : 'Upload a PDF document to read and snap notes directly.'}</p>

                      <button
                          onClick={() => fileInputRef.current?.click()}
                          className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg hover:shadow-blue-500/30 transition-all flex items-center gap-2"
                      >
                      <Upload size={20} /> {t.uploadPdf}
                      </button>
                  </div>
              ) : (
                  <PdfViewer
                     url={pdfUrl}
                     onSnap={onSnap}
                     onPageChange={onPageChange}
                     annotations={annotations}
                     onAnnotationChange={onSaveAnnotations}
                     onGenerateNotes={onGenerateNotes}
                     onDocumentReady={onDocumentReady}
                     onGuidedReading={onGuidedReading}
                     guidedReadingHighlights={guidedReadingHighlights}
                     isGuidedReadingLoading={isGuidedReadingLoading}
                     language={language}
                     userEmail={userEmail}
                     documentTitle={documentTitle}
                     boardTitle={boardTitle}
                     highlightCaptureButton={highlightCaptureButton}
                     isMobile={isMobile}
                     onVoiceCapture={onVoiceCapture}
                     selectedNoteName={selectedNoteName}
                     toolbarPortalTarget={toolbarContainer}
                  />
              )}
          </div>
      )}
    </div>
  );
};
